import { useState, type ReactElement } from "react";
import type { TradeLogEntry } from "@/types/tradeLog";

interface Props {
  trades: TradeLogEntry[];
  /** Persists the operator-entered consolidated charges for the closed
   * trades currently in view (the parent distributes it per trade and
   * refetches). The Kite console reports charges as one number per day,
   * so the card total is the natural place to enter it. */
  onBrokerageSave: (total: number) => Promise<void>;
}

/** Per-trade cap in the backend PATCH validator; the total for N closed
 * trades may not exceed N x this. */
const PER_TRADE_MAX = 5000;

interface Computed {
  total: number;
  closed: number;
  wins: number;
  /** Gross P&L from broker fills — matches the Kite console. */
  pnl: number;
  /** Consolidated charges across closed trades in view; 0 until the
   * operator enters the real figure. */
  brokerage: number;
  forceSquared: number;
}

function compute(trades: TradeLogEntry[]): Computed {
  let closed = 0;
  let wins = 0;
  let pnl = 0;
  let brokerage = 0;
  let forceSquared = 0;
  for (const t of trades) {
    if (t.exitReason === "OPEN") continue;
    closed++;
    if (t.exitReason === "FORCE_SQUAREOFF") forceSquared++;
    brokerage += t.brokerage ?? 0;
    if (t.pnl !== null) {
      pnl += t.pnl;
      if (t.pnl > 0) wins++;
    }
  }
  return { total: trades.length, closed, wins, pnl, brokerage, forceSquared };
}

function pnlColorClass(pnl: number): string {
  if (pnl > 0) return "text-success";
  if (pnl < 0) return "text-danger";
  return "text-ink";
}

export function SummaryStats({ trades, onBrokerageSave }: Readonly<Props>): ReactElement {
  const c = compute(trades);
  const winRate = c.closed === 0 ? "—" : `${String(Math.round((c.wins / c.closed) * 100))}%`;
  const net = c.pnl - c.brokerage;
  return (
    <div className="mb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <Cell label="Total trades" value={String(c.total)} />
      <Cell label="Closed" value={`${String(c.closed)} / ${String(c.total)}`} />
      <Cell label="Win rate" value={winRate} />
      <Cell
        label="Gross P&L"
        value={`${pnlSign(c.pnl)}Rs ${formatNumber(c.pnl)}`}
        valueClass={pnlColorClass(c.pnl)}
      />
      <BrokerageCell total={c.brokerage} closedCount={c.closed} onSave={onBrokerageSave} />
      <Cell
        label="Net P&L"
        value={`${pnlSign(net)}Rs ${formatNumber(net)}`}
        valueClass={pnlColorClass(net)}
      />
    </div>
  );
}

/** Sign is always explicit — a red "Rs 1,753" that is actually −1,753
 * misreads as a gain at a glance (live incident 2026-07-02). */
function pnlSign(v: number): string {
  if (v > 0) return "+";
  if (v < 0) return "-";
  return "";
}

function Cell({
  label,
  value,
  valueClass,
}: Readonly<{ label: string; value: string; valueClass?: string }>): ReactElement {
  return (
    <div className="stat-card">
      <div className="text-xs text-ink-secondary">{label}</div>
      <div className={`mt-1 text-2xl font-medium ${valueClass ?? ""}`}>{value}</div>
    </div>
  );
}

/** Editable consolidated charges. Pencil → input → save distributes the
 * total across the closed trades in view. Not editable when nothing is
 * closed (there would be no trade to attach the figure to). */
function BrokerageCell({
  total,
  closedCount,
  onSave,
}: Readonly<{
  total: number;
  closedCount: number;
  onSave: (total: number) => Promise<void>;
}>): ReactElement {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <div className="stat-card">
        <div className="text-xs text-ink-secondary">Brokerage</div>
        <BrokerageEditor
          initial={total}
          maxTotal={closedCount * PER_TRADE_MAX}
          onSave={onSave}
          onClose={() => {
            setEditing(false);
          }}
        />
      </div>
    );
  }
  return (
    <div className="stat-card">
      <div className="text-xs text-ink-secondary">Brokerage</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-medium">Rs {formatNumber(total)}</span>
        {closedCount > 0 && (
          <button
            type="button"
            className="text-ink-muted hover:text-ink-secondary"
            title="Edit total charges (from the Kite console / contract note)"
            onClick={() => {
              setEditing(true);
            }}
          >
            <i className="ti ti-pencil text-sm" />
          </button>
        )}
      </div>
    </div>
  );
}

interface DraftState {
  draft: string;
  saving: boolean;
  error: boolean;
  edit: (value: string) => void;
  save: () => Promise<void>;
}

function useBrokerageDraft(
  initial: number,
  maxTotal: number,
  onSave: (total: number) => Promise<void>,
  onClose: () => void,
): DraftState {
  const [draft, setDraft] = useState(String(Math.round(initial)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const edit = (value: string): void => {
    setDraft(value);
    setError(false);
  };
  const save = async (): Promise<void> => {
    const value = Number(draft);
    if (!Number.isFinite(value) || value < 0 || value > maxTotal) {
      setError(true);
      return;
    }
    setSaving(true);
    try {
      await onSave(value);
      onClose();
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };
  return { draft, saving, error, edit, save };
}

function BrokerageEditor({
  initial,
  maxTotal,
  onSave,
  onClose,
}: Readonly<{
  initial: number;
  maxTotal: number;
  onSave: (total: number) => Promise<void>;
  onClose: () => void;
}>): ReactElement {
  const { draft, saving, error, edit, save } = useBrokerageDraft(
    initial,
    maxTotal,
    onSave,
    onClose,
  );
  return (
    <div className="mt-1 flex items-center gap-1.5">
      <input
        type="number"
        min={0}
        max={maxTotal}
        step="1"
        value={draft}
        disabled={saving}
        autoFocus
        aria-label="Total charges in rupees"
        className={`w-24 rounded border bg-surface-2 px-2 py-1 text-lg font-medium ${
          error ? "border-danger" : "border-line"
        }`}
        onChange={(e) => {
          edit(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
          if (e.key === "Escape") onClose();
        }}
      />
      <EditorButtons saving={saving} onSaveClick={save} onClose={onClose} />
    </div>
  );
}

function EditorButtons({
  saving,
  onSaveClick,
  onClose,
}: Readonly<{
  saving: boolean;
  onSaveClick: () => Promise<void>;
  onClose: () => void;
}>): ReactElement {
  return (
    <>
      <button
        type="button"
        className="text-success"
        title="Save charges"
        disabled={saving}
        onClick={() => {
          void onSaveClick();
        }}
      >
        <i className="ti ti-check" />
      </button>
      <button
        type="button"
        className="text-ink-muted"
        title="Cancel"
        disabled={saving}
        onClick={onClose}
      >
        <i className="ti ti-x" />
      </button>
    </>
  );
}

function formatNumber(n: number): string {
  return Math.abs(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
